import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { motion } from "framer-motion";
import { Trophy, TrendingDown, Target, RefreshCw, Loader2, Download } from "lucide-react";
import { jsPDF } from "jspdf";
import { toast } from "sonner";

export default function Recap() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/recap/weekly"); setData(r.data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const downloadPdf = () => {
    if (!data) return;
    const doc = new jsPDF({ unit: "pt", format: "letter" });
    const W = doc.internal.pageSize.getWidth();
    let y = 60;
    // Header band
    doc.setFillColor(10, 11, 14); doc.rect(0, 0, W, 120, "F");
    doc.setTextColor(212, 175, 55);
    doc.setFont("helvetica", "bold"); doc.setFontSize(11);
    doc.text("AFRORICHMIND · FRIDAY RECAP", 40, 40);
    doc.setTextColor(245, 235, 230); doc.setFontSize(22);
    doc.text("This week in your money", 40, 72);
    doc.setFont("helvetica", "normal"); doc.setFontSize(11);
    doc.setTextColor(163, 168, 183);
    doc.text(`Week of ${data.week_of}`, 40, 96);
    // Stats row
    y = 160;
    doc.setTextColor(163, 168, 183); doc.setFontSize(9);
    doc.text("HEALTH SCORE", 40, y);
    doc.text("NET WORTH", 220, y);
    doc.text("SAVINGS RATE", 400, y);
    doc.setTextColor(30, 30, 30); doc.setFontSize(18); doc.setFont("helvetica", "bold");
    doc.setTextColor(212, 175, 55);
    doc.text(`${data.score}/100`, 40, y + 24);
    doc.setTextColor(30, 30, 30);
    doc.text(`$${data.net_worth.toLocaleString()}`, 220, y + 24);
    doc.setTextColor(42, 157, 143);
    doc.text(`${data.savings_rate}%`, 400, y + 24);
    // Wins
    y = 230;
    doc.setTextColor(42, 157, 143); doc.setFontSize(13); doc.setFont("helvetica", "bold");
    doc.text("Wins", 40, y);
    doc.setTextColor(30, 30, 30); doc.setFontSize(11); doc.setFont("helvetica", "normal");
    for (const w of data.wins) {
      y += 22;
      const lines = doc.splitTextToSize(`• ${w}`, W - 80);
      doc.text(lines, 50, y);
      y += (lines.length - 1) * 14;
    }
    // Drift
    y += 30;
    doc.setTextColor(224, 106, 59); doc.setFontSize(13); doc.setFont("helvetica", "bold");
    doc.text("Drift", 40, y);
    doc.setTextColor(30, 30, 30); doc.setFontSize(11); doc.setFont("helvetica", "normal");
    for (const d of data.drift) {
      y += 22;
      const lines = doc.splitTextToSize(`• ${d}`, W - 80);
      doc.text(lines, 50, y);
      y += (lines.length - 1) * 14;
    }
    // Focus
    y += 40;
    doc.setFillColor(212, 175, 55); doc.rect(40, y - 4, W - 80, 3, "F");
    y += 24;
    doc.setTextColor(212, 175, 55); doc.setFontSize(11); doc.setFont("helvetica", "bold");
    doc.text("NEXT WEEK'S ONE FOCUS", 40, y);
    y += 26;
    doc.setTextColor(30, 30, 30); doc.setFontSize(15); doc.setFont("helvetica", "normal");
    const flines = doc.splitTextToSize(data.focus, W - 80);
    doc.text(flines, 40, y);
    // Footer
    doc.setFontSize(9); doc.setTextColor(163, 168, 183);
    doc.text("Personalized to your actual money · afrorichmind.com", 40, doc.internal.pageSize.getHeight() - 30);
    doc.save(`AfroRichMind-Recap-${data.week_of.replace(/[^a-z0-9]/gi, "-")}.pdf`);
    toast.success("Recap saved");
  };

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-widest text-[color:var(--gold)] mb-2">Friday Recap</div>
          <h1 className="font-heading text-3xl lg:text-4xl font-bold">This week in your money</h1>
          {data && <div className="text-[color:var(--text-secondary)] mt-2 text-sm">Week of {data.week_of}</div>}
        </div>
        <div className="flex gap-2">
          <button data-testid="regenerate-recap" onClick={load} disabled={loading} className="px-4 py-2 rounded-full border border-[rgba(212,175,55,0.2)] hover:border-[color:var(--gold)] text-xs transition inline-flex items-center gap-2 disabled:opacity-50">
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Regenerate
          </button>
          <button data-testid="download-pdf" onClick={downloadPdf} disabled={!data} className="gold-btn px-4 py-2 rounded-full text-xs inline-flex items-center gap-2 disabled:opacity-50">
            <Download className="w-3 h-3" /> Save as PDF
          </button>
        </div>
      </div>

      {loading || !data ? (
        <div className="glass-card p-16 text-center"><Loader2 className="w-8 h-8 animate-spin text-[color:var(--gold)] mx-auto" /><p className="text-xs text-[color:var(--text-muted)] mt-4">Your coach is drafting your recap...</p></div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="glass-card p-5">
              <div className="text-xs uppercase tracking-widest text-[color:var(--text-muted)] mb-2">Health Score</div>
              <div data-testid="recap-score" className="font-mono text-2xl font-bold gold-gradient-text">{data.score}/100</div>
            </div>
            <div className="glass-card p-5">
              <div className="text-xs uppercase tracking-widest text-[color:var(--text-muted)] mb-2">Net Worth</div>
              <div className="font-mono text-2xl font-bold">${data.net_worth.toLocaleString()}</div>
            </div>
            <div className="glass-card p-5">
              <div className="text-xs uppercase tracking-widest text-[color:var(--text-muted)] mb-2">Savings Rate</div>
              <div className="font-mono text-2xl font-bold text-[color:var(--emerald)]">{data.savings_rate}%</div>
            </div>
          </div>

          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            data-testid="recap-wins" className="glass-card p-6 mb-4 border-[rgba(42,157,143,0.25)]">
            <div className="flex items-center gap-2 mb-4"><Trophy className="w-5 h-5 text-[color:var(--emerald)]" /><h2 className="font-heading text-lg font-semibold">Wins</h2></div>
            <ul className="space-y-3">
              {data.wins.map((w, i) => (
                <motion.li key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.1 }}
                  className="flex items-start gap-3 text-[color:var(--text-primary)] leading-relaxed">
                  <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--emerald)] mt-2.5 shrink-0" /> {w}
                </motion.li>
              ))}
            </ul>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            data-testid="recap-drift" className="glass-card p-6 mb-4 border-[rgba(224,106,59,0.25)]">
            <div className="flex items-center gap-2 mb-4"><TrendingDown className="w-5 h-5 text-[color:var(--terracotta)]" /><h2 className="font-heading text-lg font-semibold">Drift</h2></div>
            <ul className="space-y-3">
              {data.drift.map((d, i) => (
                <motion.li key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 + i * 0.1 }}
                  className="flex items-start gap-3 text-[color:var(--text-primary)] leading-relaxed">
                  <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--terracotta)] mt-2.5 shrink-0" /> {d}
                </motion.li>
              ))}
            </ul>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            data-testid="recap-focus" className="glass-card p-8 border-[rgba(212,175,55,0.4)] relative overflow-hidden">
            <div className="absolute inset-0 shimmer pointer-events-none" />
            <div className="relative">
              <div className="flex items-center gap-2 mb-4"><Target className="w-5 h-5 text-[color:var(--gold)]" /><h2 className="font-heading text-lg font-semibold">Next week's one focus</h2></div>
              <p className="font-heading text-xl lg:text-2xl leading-snug">{data.focus}</p>
            </div>
          </motion.div>
        </>
      )}
    </div>
  );
}
